// Binds the side panel to the store. Listeners write to the store; the store
// subscription writes back to the DOM (idempotent). The curve grid, colour
// swatches and plate buttons are built from data at mount time.
import type { App } from '../app.ts';
import type { AppState, ColorMode, Detail } from '../types.ts';
import { $ } from './dom.ts';
import { PREF, safeSet } from '../state/prefs.ts';
import { PLATES } from '../plates.ts';
import { aspectK } from '../render/camera.ts';
import { GRADIENT_PRESETS } from '../gradient/presets.ts';
import { classicalCurves, displayFor, GENERIC_ICON, variantLabel } from './curve-meta.ts';
import type { Projection } from '../types.ts';

/** Presets offered as swatches; the gradient editor has the full list. */
const SWATCHES = ['roygbiv', 'viridis', 'magma', 'ice'];

const pressed = (el: Element, on: boolean) => el.setAttribute('aria-pressed', String(on));

export function mountPanel(app: App): void {
  const grid = $('curveGrid');
  const rngWidth = $<HTMLInputElement>('rngWidth');
  const lblWidth = $('lblWidth');
  const btnRevGrad = $('btnRevGrad');
  const modeButtons = Array.from($('modeRow').querySelectorAll<HTMLButtonElement>('button[data-mode]'));
  const swatchRow = $('swatchRow');
  const solidRow = $('solidRow');
  const colSolid = $<HTMLInputElement>('colSolid');
  const colBg = $<HTMLInputElement>('colBg');
  const bgTheme = $('bgTheme'), bgWhite = $('bgWhite'), bgBlack = $('bgBlack');
  const rngAspect = $<HTMLInputElement>('rngAspect');
  const lblAspect = $('lblAspect');
  const lblLegs = $('lblLegs');
  const cameraSection = $('cameraSection');
  const btnSpin = $('btnSpin');
  const rngStart = $<HTMLInputElement>('rngStart');
  const lblStart = $('lblStart');
  const selDetail = $<HTMLSelectElement>('selDetail');
  const selShotScale = $<HTMLSelectElement>('selShotScale');
  const chkTransparent = $<HTMLInputElement>('chkTransparent');
  const chkFullDetail = $<HTMLInputElement>('chkFullDetail');
  const plateNote = $('plateNote');
  const plateList = $('plateList');
  const extraCurve = $<HTMLSelectElement>('extraCurve');
  const variantRow = $('variantRow');
  const selVariant = $<HTMLSelectElement>('selVariant');
  const tilingSection = $('tilingSection');
  const chkTiles = $<HTMLInputElement>('chkTiles');
  const tileOptions = $('tileOptions');
  const chkTileHighlight = $<HTMLInputElement>('chkTileHighlight');
  const rngTileOpacity = $<HTMLInputElement>('rngTileOpacity');
  const lblTileOpacity = $('lblTileOpacity');
  const tilingStatus = $('tilingStatus');
  const tileTipNote = $('tileTipNote');
  const chkEndpoints = $<HTMLInputElement>('chkEndpoints');
  const dragButtons = Array.from($('modeRow3d').querySelectorAll<HTMLButtonElement>('button[data-drag]'));
  const selProjection = $<HTMLSelectElement>('selProjection');
  const chkXray = $<HTMLInputElement>('chkXray');
  const chkCage = $<HTMLInputElement>('chkCage');
  const rngOpacity = $<HTMLInputElement>('rngOpacity');
  const lblOpacity = $('lblOpacity');

  // ---- curve grid ----
  const curveButtons = new Map<string, HTMLButtonElement>();
  for (const c of app.orderedCurves()) {
    const d = displayFor(c.id);
    const dims = c.exports[0]?.dims ?? d?.dims ?? 2;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'curve-choice';
    b.dataset.key = d?.key ?? c.id;
    b.title = d?.blurb ?? c.name;
    b.innerHTML = `<svg viewBox="0 0 36 36" aria-hidden="true"><path d=""/></svg><span><span class="name"></span><span class="dim">${dims} dimensions</span></span>`;
    b.querySelector('path')!.setAttribute('d', d?.icon ?? GENERIC_ICON);
    b.querySelector('.name')!.textContent = d?.name ?? c.name;
    b.addEventListener('click', () => app.switchCurve(c.id));
    grid.appendChild(b);
    curveButtons.set(c.id, b);
  }

  // ---- classical curves dropdown ----
  const classical = classicalCurves().filter((d) => app.manifest.curves.some((c) => c.id === d.id));
  extraCurve.innerHTML = '';
  const placeholder = document.createElement('option');
  placeholder.value = ''; placeholder.textContent = 'More classical curves…';
  extraCurve.appendChild(placeholder);
  for (const d of classical) { const o = document.createElement('option'); o.value = d.id; o.textContent = d.name; extraCurve.appendChild(o); }
  extraCurve.hidden = classical.length === 0;
  extraCurve.addEventListener('change', () => { if (extraCurve.value) app.switchCurve(extraCurve.value); });

  // ---- swatches ----
  const swatchButtons: { preset: string; el: HTMLButtonElement }[] = [];
  for (const preset of SWATCHES) {
    const stops = GRADIENT_PRESETS[preset];
    if (!stops) continue;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'swatch';
    b.title = preset;
    b.setAttribute('aria-label', preset + ' gradient');
    const i = document.createElement('i');
    i.style.background = `linear-gradient(90deg, ${stops.join(', ')})`;
    b.appendChild(i);
    b.addEventListener('click', () => {
      const g = app.state.grad;
      app.store.patch({ colorMode: 'roygbiv', grad: { ...g, preset, stops: stops.slice() } });
    });
    swatchRow.appendChild(b);
    swatchButtons.push({ preset, el: b });
  }

  // ---- plates ----
  for (const P of PLATES) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = P.label;
    b.title = P.note;
    b.addEventListener('click', () => app.applyState({ ...P, plate: P.id }));
    plateList.appendChild(b);
  }

  // ---- store -> DOM ----
  const sync = (s: AppState) => {
    for (const [id, b] of curveButtons) pressed(b, id === s.curve);
    rngWidth.value = String(s.width);
    lblWidth.textContent = s.width.toFixed(1) + ' px';
    for (const b of modeButtons) pressed(b, b.dataset.mode === s.colorMode);
    for (const w of swatchButtons) pressed(w.el, s.colorMode === 'roygbiv' && s.grad.preset === w.preset);
    pressed(btnRevGrad, s.grad.reverse);
    solidRow.hidden = s.colorMode !== 'solid';
    colSolid.value = s.solidHex;
    colBg.value = s.bgHex;
    const bg = s.bgHex.toLowerCase();
    pressed(bgTheme, bg === app.themeBg.toLowerCase());
    pressed(bgWhite, bg === '#ffffff');
    pressed(bgBlack, bg === '#000000');
    rngAspect.value = String(s.aspect);
    const k = aspectK(s.aspect);
    lblAspect.textContent = k.toFixed(2);
    // canonical pinwheel prototile has legs 2 and 1; diag(k, 1/k) sends those
    // to 2k and 1/k, so the leg ratio becomes 2k^2 : 1
    lblLegs.textContent = s.curve.indexOf('pinwheel') >= 0 ? '· legs ' + (2 * k * k).toFixed(2) + ' : 1' : '';
    selDetail.value = String(s.detail);
    selShotScale.value = String(s.shot.scale);
    chkTransparent.checked = s.shot.transparent;
    chkFullDetail.checked = s.shot.fullDetail;
    const plate = PLATES.find((p) => p.id === s.plate);
    plateNote.textContent = plate ? plate.label + ' — ' + plate.note : '';
    cameraSection.hidden = !app.is3d();
    extraCurve.value = classical.some((d) => d.id === s.curve) ? s.curve : '';
    const variants = app.variants();
    variantRow.hidden = variants.length < 2;
    if (variants.length >= 2) {
      const cur = app.currentVariant();
      if (selVariant.options.length !== variants.length || Array.from(selVariant.options).some((o, i) => o.value !== variants[i])) {
        selVariant.innerHTML = '';
        for (const v of variants) { const o = document.createElement('option'); o.value = v; o.textContent = variantLabel(v); selVariant.appendChild(o); }
      }
      selVariant.value = cur;
    }
    chkTiles.checked = s.tiles.show;
    chkTiles.disabled = !app.hasTiles();
    tileOptions.hidden = !s.tiles.show || !app.hasTiles();
    chkTileHighlight.checked = s.tiles.highlight;
    rngTileOpacity.value = String(s.tiles.opacity);
    lblTileOpacity.textContent = Math.round(s.tiles.opacity * 100) + '%';
    chkEndpoints.checked = s.endpoints;
    for (const b of dragButtons) pressed(b, b.dataset.drag === s.view3d.mode);
    selProjection.value = s.view3d.projection;
    chkXray.checked = s.view3d.xray;
    chkCage.checked = s.view3d.cage;
    rngOpacity.value = String(s.view3d.opacity);
    lblOpacity.textContent = Math.round(s.view3d.opacity * 100) + '%';
  };
  // Things that live outside the store (camera spin, draw range).
  const syncLive = () => {
    pressed(btnSpin, app.spin);
    const start = Math.round(app.range.start * 1000) / 10;
    if (document.activeElement !== rngStart) rngStart.value = String(start);
    lblStart.textContent = start + '%';
    cameraSection.hidden = !app.is3d();
    tilingStatus.textContent = app.tileStatus || (app.hasTiles() ? 'Leaf tiles at the selected iteration.' : 'Tiling is available for the six main curves.');
    tileTipNote.hidden = !(app.state.tiles.show && app.state.tiles.highlight && app.tiles && app.tileTipOutside);
    void tilingSection;
  };

  // ---- DOM -> store ----
  rngWidth.addEventListener('input', () => {
    safeSet(PREF.width, rngWidth.value);
    app.store.patch({ width: parseFloat(rngWidth.value) });
  });
  btnRevGrad.addEventListener('click', () => app.store.patch({ grad: { ...app.state.grad, reverse: !app.state.grad.reverse } }));
  for (const b of modeButtons) b.addEventListener('click', () => app.store.patch({ colorMode: b.dataset.mode as ColorMode }));
  colSolid.addEventListener('input', () => { safeSet(PREF.solidHex, colSolid.value); app.store.patch({ solidHex: colSolid.value }); });
  const setBg = (hex: string) => { safeSet(PREF.bgHex, hex); app.store.patch({ bgHex: hex }); };
  colBg.addEventListener('input', () => setBg(colBg.value));
  bgTheme.addEventListener('click', () => setBg(app.themeBg));
  bgWhite.addEventListener('click', () => setBg('#ffffff'));
  bgBlack.addEventListener('click', () => setBg('#000000'));
  const setAspect = (v: number) => { app.store.patch({ aspect: v }); app.resetView(); };
  rngAspect.addEventListener('input', () => setAspect(parseFloat(rngAspect.value) || 0));
  $('btnAspect1').addEventListener('click', () => setAspect(0));

  for (const b of cameraSection.querySelectorAll<HTMLButtonElement>('button[data-view]')) {
    b.addEventListener('click', () => app.setView(b.dataset.view as 'iso' | 'front' | 'side' | 'top'));
  }
  btnSpin.addEventListener('click', () => app.setSpin(!app.spin));
  $('btnResetCam').addEventListener('click', () => { app.setSpin(false); app.store.patch({ view3d: { ...app.state.view3d, projection: 'persp' } }); app.resetView(); });

  selVariant.addEventListener('change', () => app.setVariant(selVariant.value));
  chkTiles.addEventListener('change', () => app.store.patch({ tiles: { ...app.state.tiles, show: chkTiles.checked } }));
  chkTileHighlight.addEventListener('change', () => app.store.patch({ tiles: { ...app.state.tiles, highlight: chkTileHighlight.checked } }));
  rngTileOpacity.addEventListener('input', () => app.store.patch({ tiles: { ...app.state.tiles, opacity: parseFloat(rngTileOpacity.value) } }));
  chkEndpoints.addEventListener('change', () => app.store.patch({ endpoints: chkEndpoints.checked }));
  for (const b of dragButtons) b.addEventListener('click', () => app.store.patch({ view3d: { ...app.state.view3d, mode: b.dataset.drag as 'orbit' | 'pan' } }));
  selProjection.addEventListener('change', () => { app.store.patch({ view3d: { ...app.state.view3d, projection: selProjection.value as Projection } }); if (app.isFitted()) app.fitView(); });
  chkXray.addEventListener('change', () => app.store.patch({ view3d: { ...app.state.view3d, xray: chkXray.checked } }));
  chkCage.addEventListener('change', () => app.store.patch({ view3d: { ...app.state.view3d, cage: chkCage.checked } }));
  rngOpacity.addEventListener('input', () => app.store.patch({ view3d: { ...app.state.view3d, opacity: parseFloat(rngOpacity.value) } }));

  rngStart.addEventListener('input', () => {
    app.pause();
    const v = parseFloat(rngStart.value) / 100;
    app.setRange(v, Math.max(app.range.end, Math.min(1, v + 0.001)));
  });
  $('btnShowAll').addEventListener('click', () => app.showAll());

  selDetail.addEventListener('change', () => {
    const v = selDetail.value;
    const detail: Detail = v === 'auto' ? 'auto' : (parseInt(v, 10) as Detail);
    safeSet(PREF.detail, v);
    app.store.patch({ detail });
  });
  $('btnFullDetail').addEventListener('click', () => app.loop.forceFullDetail());
  selShotScale.addEventListener('change', () => {
    const v = selShotScale.value;
    safeSet(PREF.shotScale, v);
    app.store.patch({ shot: { ...app.state.shot, scale: v === 'max' ? 'max' : parseFloat(v) || 1 } });
  });
  chkTransparent.addEventListener('change', () => app.store.patch({ shot: { ...app.state.shot, transparent: chkTransparent.checked } }));
  chkFullDetail.addEventListener('change', () => app.store.patch({ shot: { ...app.state.shot, fullDetail: chkFullDetail.checked } }));

  app.store.subscribe(sync);
  app.listeners.add(syncLive);
  sync(app.state);
  syncLive();
}
